import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  CircularProgress,
  IconButton
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';

/**
 * List of folders in the current location.
 * Clicking a folder navigates to that folder (caller provides onFolderClick).
 * When sharedWithMeMode is true, rename/delete only shown when folder.effectiveAccessLevel === 'edit'.
 * Share (manage access) is shown only when !sharedWithMeMode.
 */
export default function FolderList({
  folders = [],
  loading,
  onFolderClick,
  onRenameFolder,
  onDeleteFolder,
  onManageAccess,
  sharedWithMeMode = false
}) {
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 120,
          py: 2
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!folders || folders.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No folders
        </Typography>
      </Box>
    );
  }

  return (
    <List disablePadding>
      {folders.map((folder) => {
        const id = folder._id ?? folder.id;
        const name = folder.name ?? 'Folder';
        const canEdit =
          !sharedWithMeMode || folder.effectiveAccessLevel === 'edit';
        const showManageAccess = !sharedWithMeMode;
        return (
          <ListItem
            key={id}
            disablePadding
            sx={{ mb: 0.5, borderRadius: 1 }}
            secondaryAction={
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {showManageAccess && (
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onManageAccess?.(folder);
                    }}
                    aria-label="Manage access"
                  >
                    <PeopleAltOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
                {canEdit && (
                  <>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRenameFolder?.(folder);
                      }}
                      aria-label="Rename"
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      edge="end"
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteFolder?.(folder);
                      }}
                      aria-label="Delete"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
            }
          >
            <ListItemButton
              onClick={() => onFolderClick(id)}
              sx={{ borderRadius: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <FolderIcon color="action" />
              </ListItemIcon>
              <ListItemText primary={name} />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}
